declare global {
  interface Window {
    dataLayer: unknown[];
  }
}

export const STORAGE_KEY = "cookie_consent";
export const GOOGLE_ADS_ID = "AW-18456140215";
export const AFFILIATE_CLICK_SEND_TO = "AW-18456140215/jY3yCLPinvocELezyeBE";

const CONSENT_COUNTRIES = [
  "AT", "BE", "BG", "HR", "CY", "CZ", "DK", "EE", "FI", "FR", "DE", "GR", "HU",
  "IE", "IT", "LV", "LT", "LU", "MT", "NL", "PL", "PT", "RO", "SK", "SI", "ES",
  "SE", "IS", "LI", "NO", "GB", "CH", "CA",
];

let consentRegion: Promise<boolean> | undefined;

const lookupConsentRegion = async (): Promise<boolean> => {
  try {
    const res = await fetch("/cdn-cgi/trace", { signal: AbortSignal.timeout(2000) });
    if (!res.ok) return true;
    const country = (await res.text()).match(/^loc=([A-Z0-9]{2})$/m)?.[1];
    if (!country || country === "XX" || country === "T1") return true;
    return CONSENT_COUNTRIES.includes(country);
  } catch {
    return true;
  }
};

export const isConsentRequiredRegion = (): Promise<boolean> =>
  (consentRegion ??= lookupConsentRegion());

export const hasAdConsent = (): boolean =>
  localStorage.getItem(STORAGE_KEY) === "granted";

export const canTrackAds = async (): Promise<boolean> => {
  const choice = localStorage.getItem(STORAGE_KEY);
  if (choice !== null) return choice === "granted";
  const required = await isConsentRequiredRegion();
  const latest = localStorage.getItem(STORAGE_KEY);
  return latest !== null ? latest === "granted" : !required;
};

export const updateConsent = (decision: "granted" | "denied"): void => {
  if (decision === "denied") window.gtag?.("set", "user_data", null);
  window.gtag?.("consent", "update", {
    ad_storage: decision,
    ad_user_data: decision,
    ad_personalization: decision,
  });
};

/**
 * Loads the Google Ads tag using Advanced Consent Mode: the tag always loads,
 * but visitors without consent only generate cookieless pings.
 * Memoized so concurrent calls share a single load — the script tag guard
 * alone is racy because the region lookup yields before it runs.
 */
let loadGoogleAdsPromise: Promise<void> | undefined;

export const loadGoogleAds = (): Promise<void> =>
  (loadGoogleAdsPromise ??= doLoadGoogleAds());

const doLoadGoogleAds = async (): Promise<void> => {
  if (typeof window === "undefined") return;
  const allowed = await canTrackAds();
  window.dataLayer = window.dataLayer || [];
  const push = (...args: unknown[]) => {
    (window.gtag as unknown as (...a: unknown[]) => void)?.(...args);
  };
  if (!window.gtag) {
    window.gtag = ((...args: unknown[]) => {
      window.dataLayer.push(args);
    }) as unknown as Window["gtag"];
  }
  updateConsent(allowed ? "granted" : "denied");
  if (document.querySelector("script[data-google-ads]")) return;
  push("js", new Date());
  push("config", GOOGLE_ADS_ID);

  const script = document.createElement("script");
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GOOGLE_ADS_ID}`;
  script.async = true;
  script.dataset.googleAds = "";
  document.head.appendChild(script);
};
